import type { CSSProperties, ReactNode } from "react";

interface Props {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  /** Optional all-caps suffix shown after the label, e.g., "REQUIRED" */
  suffix?: ReactNode;
  style?: CSSProperties;
}

/**
 * Canonical form field. Mono uppercase label + spaced sublabel hint.
 * Matches the SCADA-style identity of the rest of the system: small caps,
 * letter-spaced label, hairline input, no rounded fluff.
 */
export function FormField({ label, children, hint, suffix, style }: Props) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            textTransform: "uppercase",
            letterSpacing: "0.16em",
            color: "var(--text-muted)",
            fontWeight: 500,
          }}
        >
          {label}
        </label>
        {suffix ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              letterSpacing: "0.14em",
              color: "var(--accent)",
              textTransform: "uppercase",
            }}
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {children}
      {hint ? (
        <span
          style={{
            fontSize: 10.5,
            color: "var(--text-muted)",
            lineHeight: 1.5,
            letterSpacing: "-0.005em",
          }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/* Shared input baseline styles used across the form pages. */
export const inputStyle: CSSProperties = {
  background: "var(--bg-input)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-input)",
  padding: "9px 12px",
  color: "var(--text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
  outline: "none",
  letterSpacing: "-0.005em",
};

export const monoInputStyle: CSSProperties = {
  ...inputStyle,
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  letterSpacing: "0.04em",
};

/* Read-only "display" variant — looks like an input but isn't editable. */
export const readonlyValueStyle: CSSProperties = {
  ...inputStyle,
  background: "var(--bg-tertiary)",
  color: "var(--text-secondary)",
  cursor: "default",
  userSelect: "all",
};

export const readonlyMonoStyle: CSSProperties = {
  ...readonlyValueStyle,
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  letterSpacing: "0.04em",
  wordBreak: "break-all",
};
